import React, {useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import AnimatedNumber from "animated-number-react";
import {ProgressBar} from "react-step-progress-bar";
import {Img} from "react-image";
import LoadingSpinner from "../../components/LoadingSpinner";
import useMediaQuery from "../../hooks/useMediaQuery";
import TalentCandidate from "../../models/TalentCandidate";
import {Link, useLocation, useNavigate, useSearchParams} from "react-router-dom";
import UserImageCircle from "../../components/UserImageCircle";
import AiResumeScoreCallout from "./AiResumeScoreCallout";


const OPTIONS = [
    { title: 'Accomplished', description: '< 6 Years', key: 'wants_junior_experience' },
    { title: 'Advanced', description: '6-10 Years', key: 'wants_intermediate_experience' },
    { title: 'Expert', description: '11+ Years', key: 'wants_senior_experience' },
]
const expColumns = [
    {
        title: 'Meets Criteria?',
        dataIndex: 'meets_criteria',
        key: 'meets_criteria',
        className: 'text-center',
        render: (meets_criteria, _) => (_.match ? (
            <i className={TB.mapClass('fas ', {
                "fa-times text-danger": !meets_criteria,
                "fa-check text-success": meets_criteria,
            })}/>
        ) : '-'),
    },
    {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
    },
    {
        title: 'Experience',
        dataIndex: 'total_experience_label',
        key: 'total_experience_label',
        render: (total_experience_label, record) => (
            <p className="font-semibold">{total_experience_label}</p>
        ),
    },
];

const MetricDetailRow = ({metric, name}) => {

    return (
        <div className="flex flex-row justify-between m-0 gap-1">
            <div className="inline-flex flex-row m-0 gap-1">
                <p><strong>{metric.count}</strong></p>
                <p><strong>of</strong></p>
                <p><strong>{metric.max_count}</strong></p>
            </div>
            <p className="flex-grow p-0">{name}</p>
        </div>
    )
}

const TalentMatchDetails = props => {

    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const isMobile = useMediaQuery("(max-width: 992px)");

    const talent = props.talent;
    const talentCriteria = props.criteria;
    // let metrics = props.talent.metricScores();
    const [currentMatchSection, setCurrentMatchSection] = useState(0);


    const [metrics] = useState(() => {
        if (!talent) return []

        return talent.matchScoresForCriteria(talentCriteria)
    });


    const matchedDisciplines = useMemo(() => {
        if (props.criteria.templates?.length && props.criteria.matching?.position_templates?.templates?.length) {
            return props.criteria.matching?.position_templates.templates
        }
        let disciplines = props.criteria.disciplines || [];
        if (!disciplines?.length && props.criteria.discipline) {
            disciplines = [props.criteria.discipline]
        }
        return  disciplines?.length ? props.talent.matchedDisciplines(disciplines) : [];
    }, [props.criteria?.disciplines, props.criteria?.templates]);

    const detailsLink = useMemo(() => {
        let params = new URLSearchParams(searchParams);
        if (props.positionId) {
            params.set('role', props.positionId);
        }

        return {
            pathname: location.pathname + `/talent-profiles/${props.talent.id}/match`,
            search: params.size > 0 ? `?${params.toString()}` : undefined
        }
    }, [props.positionId, searchParams?.toString()])

    const resumeMatchScore = props.talent.resumeMatchScore?.();

    return (
        <div className="talent-match-details">
            {!props.hideHeader && (
                <div className="talent-match-title">

                    <div className="talent-match-banner">
                        <Img
                            loader={(<div className="img-fluid img-loader"><LoadingSpinner/></div>)}
                            unloader={<UserImageCircle user={props.talent} hideTooltip={true} size={45}/>}
                            src={props.talent.image_path}
                            className="img-grayscale img-fluid"/>

                        <h5>{TB.truncate(props.talent.firstNamePlusInitial(), 25)}</h5>
                        <p>{TB.truncate(props.talent.job_title, 40)}</p>
                    </div>
                    <h4>
                        Match Summary
                    </h4>
                </div>
            )}
            {/* Above the criteria circles and ruled off from them: those three
                are what the total match is made of, this is a separate AI read.
                Leading with it, rather than tucking it underneath, stops it
                looking like a fourth contributor to the number below.

                The inset is set here because `.talent-match-details` has no
                horizontal padding of its own — 25px matches `.match-footer`. */}
            {resumeMatchScore !== null && resumeMatchScore !== undefined && (
                <div className="mx-[25px] mt-4 border-b border-[#E2E3EA] pb-5">
                    <AiResumeScoreCallout score={resumeMatchScore} />
                </div>
            )}

            <div className="metric-container">

                <React.Fragment>
                    <div className={
                        TB.mapClass("metric-match", {disabled: !metrics.skills_match.skills_max_count})
                    }>
                        <p>
                            <span><span>Skill</span><span>Expertise</span></span>
                            <small>
                                <span>(<b>{metrics.skills_match.skills_max_count}&nbsp;</b> Skills Selected)</span>
                            </small>
                        </p>

                        {talent.progressCircleForMetric({
                            percent: metrics.skills_match.percent,
                            count: metrics.skills_match.skills_count,
                            overall : {
                                max_count: metrics.skills_match.skills_max_count,

                            }})}
                        <div className="section-body">
                            <p className="section-heading">
                                <strong>
                                    {`${metrics.skills_match.skills_max_count > 0 ? Math.ceil(metrics.skills_match.percent) : 0}% Skills Match`}
                                </strong>
                            </p>

                            {metrics.skills_match.skills?.very_important && (
                                <MetricDetailRow
                                    metric={metrics.skills_match.skills?.very_important}
                                    name={`Very Important Skills`}/>
                            )}

                            {metrics.skills_match.skills?.important && (
                                <MetricDetailRow
                                    metric={metrics.skills_match.skills?.important}
                                    name={`Important Skills`}/>
                            )}
                            {metrics.skills_match.skills?.nice_to_have && (
                                <MetricDetailRow
                                    metric={metrics.skills_match.skills?.nice_to_have}
                                    name={`Nice to Have Skills`}/>
                            )}
                        </div>
                    </div>
                    <div className={
                        TB.mapClass("metric-match", {disabled: !metrics.experience_match.disciplines_max_count})
                    }>
                        <p>
                            <span><span>Years</span><span>Experience</span></span>
                            <small>
                                {metrics.experience_match.experience ? (
                                    <span>(<b>{TB.filter(TB.values(metrics.experience_match.experience), value => value)?.length}&nbsp;</b> Experience Levels Selected)</span>
                                ) : (
                                    <span>(<b>{metrics.experience_match.disciplines_max_count}&nbsp;</b> Roles Selected)</span>
                                )}
                            </small>
                        </p>

                        {talent.progressCircleForMetric({
                            percent: metrics.experience_match.percent,
                            count: metrics.experience_match.disciplines_count,
                            overall : {
                                max_count: metrics.experience_match.disciplines_max_count,

                            }})}

                        <div className="section-body">
                            <p className="section-heading">
                                <strong>
                                    {`${Math.ceil(metrics.experience_match.percent)}% Meets Requested`}
                                </strong>
                            </p>

                            {props.talent &&
                                props.talent instanceof TalentCandidate &&
                                matchedDisciplines.length > 0 && (
                                    <>
                                        {TB.map(TB.take(matchedDisciplines, 3),discipline => {
                                            let score = false;
                                            if (discipline.self_score) {
                                                score = laravel.talent_experience_levels[discipline.self_score];
                                            }

                                            return (
                                                <div key={discipline.id} className="flex flex-row justify-between mx-0 relative self-stretch metric-detail-row gap-3">
                                                    <p className="mb-0 inline whitespace-nowrap overflow-hidden text-ellipsis max-w-[170px]">
                                                        {discipline.name}
                                                    </p>
                                                    <div className="flex-grow flex flex-row items-center justify-end ml-auto p-0 text-center gap-3">
                                                        {/*{score?.level && (*/}

                                                        {/*    <p className="mb-0 inline">*/}
                                                        {/*        {score.level}*/}
                                                        {/*    </p>*/}
                                                        {/*)}*/}
                                                        <p className="mb-0 inline">
                                                            <small className="text-primary text-bold">
                                                                {score ? score.label : 'None'}
                                                            </small>
                                                        </p>
                                                    </div>
                                                </div>
                                            )
                                        })}
                                        {matchedDisciplines.length > 3 && (
                                            <p className="metric-detail-more">
                                                <small>
                                                    ...plus {matchedDisciplines.length - 3} more
                                                </small>
                                            </p>
                                        )}
                                    </>
                                )}
                        </div>
                    </div>

                    <div className={
                        TB.mapClass("metric-match", {disabled: !(
                            (metrics.industries_brands_match.industries_max_count || 0) +
                                (metrics.industries_brands_match.brands_max_count || 0)
                            )})
                    }>
                        <p>
                            <span>
                                <span>
                                    Industry/Brand
                                </span>
                                <span>Expertise</span></span>
                            <small>
                                <span>
                                    (
                                    {metrics.industries_brands_match.industries_max_count > 0 && (
                                        <React.Fragment>
                                            <b>{metrics.industries_brands_match.industries_max_count}</b>&nbsp;Industries
                                        </React.Fragment>
                                    )}
                                    {metrics.industries_brands_match.industries_max_count > 0 &&
                                        metrics.industries_brands_match.brands_max_count > 0 && <React.Fragment>&nbsp;/&nbsp;</React.Fragment>}

                                    {metrics.industries_brands_match.brands_max_count > 0 && (
                                        <React.Fragment>
                                            <b>{metrics.industries_brands_match.brands_max_count}</b>&nbsp;Brands
                                        </React.Fragment>
                                    )}
                                    &nbsp;Selected)
                                </span>
                            </small>
                        </p>


                        {talent.progressCircleForMetric({
                            percent: metrics.industries_brands_match.percent,
                            count: (metrics.industries_brands_match.industries_count || 0) + (metrics.industries_brands_match.brands_count || 0),
                            overall : {
                                max_count: (metrics.industries_brands_match.industries_max_count || 0) + (metrics.industries_brands_match.brands_max_count || 0),
                            }})}
                        <div className="section-body">
                            {metrics.industries_brands_match.industries_max_count > 0 && (
                                <React.Fragment>
                                    <p className="section-heading">
                                        <strong>
                                            {`${metrics.industries_brands_match.industries_max_count > 0 ? Math.ceil(metrics.industries_brands_match.industries_percent) : 0}% Industries Match`}
                                        </strong>
                                    </p>

                                    {metrics.industries_brands_match.industries?.very_important && (
                                        <MetricDetailRow
                                            metric={metrics.industries_brands_match.industries?.very_important}
                                            name={`Very Important Industries`}/>
                                    )}

                                    {metrics.industries_brands_match.industries?.important && (
                                        <MetricDetailRow
                                            metric={metrics.industries_brands_match.industries?.important}
                                            name={`Important Industries`}/>
                                    )}
                                    {metrics.industries_brands_match.industries?.nice_to_have && (
                                        <MetricDetailRow
                                            metric={metrics.industries_brands_match.industries?.nice_to_have}
                                            name={`Nice to Have Industries`}/>
                                    )}
                                </React.Fragment>
                            )}


                            {metrics.industries_brands_match.brands_max_count > 0 && (
                                <React.Fragment>
                                    <p className={TB.mapClass("section-heading",{
                                        "mt-3": metrics.industries_brands_match.industries_max_count > 0
                                    })}>
                                        <strong>
                                            {`${Math.ceil(metrics.industries_brands_match.brands_percent)}% Brands Match`}
                                        </strong>
                                    </p>
                                    <MetricDetailRow
                                        metric={{
                                            count: metrics.industries_brands_match.brands_count,
                                            max_count: metrics.industries_brands_match.brands_max_count,
                                        }}
                                        name={`Matching Brands`}/>
                                </React.Fragment>
                            )}

                        </div>
                    </div>

                </React.Fragment>
            </div>

            <div className="match-footer flex flex-row mt-4 mx-0 gap-3">
                <div className="flex-grow max-w-[300px]">
                    <div className="browse-talent-matching">

                        <p className="overall-progress">
                            <AnimatedNumber
                                value={props.talent.matching.overall.percent}
                                formatValue={(value) => `${TB.round(value)}% Total Match`}
                                duration={1500}/>
                        </p>

                        <ProgressBar
                            filledBackground={TB.config('color.orange')}
                            height={20}
                            percent={props.talent.matching.overall.percent}
                            unfilledBackground={TB.config('color.gray')}/>
                    </div>
                </div>
                <div className="ml-auto items-end">
                    <Link to={detailsLink} onClick={async() => {
                        await TB.event('Modal@hide', true);
                    }} className="btn btn-primary btn-block">See Match Details</Link>
                </div>
            </div>

        </div>
    );
};

TalentMatchDetails.propTypes = {
    criteria: PropTypes.object,
    talent: PropTypes.object,
    projectId: PropTypes.any,
    positionId: PropTypes.any,
    isEngagementLead: PropTypes.any,
};

export default TalentMatchDetails;